import { ref } from "vue";
import { defineStore } from "pinia";
import { ApiCallError, resolveDataspaceAlias, resolveProcessAlias } from "@/api";
import { normalizeAlias } from "@/shared/utils/alias";

/**
 * Which alias names which dataspace, and which process of a dataspace — for the Operator's URLs and
 * nothing else (`shared/utils/alias.ts`). The route turns an alias into the id once
 * (`utils/_shared/operatorIds.ts`), and everything below it works with ids; this is what the route
 * asks first, and what a link asks for the segment to write (`dataspaceSegment`, `processSegment`).
 *
 * FILLED FROM WHAT THE OPERATOR READS ANYWAY: every dataspace and process row carries its alias, and
 * the views hand those here (`rememberDataspace`, `rememberProcesses`), as do the route's own
 * lookups and what the dataspace room says was renamed. So a link from a list is written with the
 * alias already, and opening it asks the server nothing.
 *
 * AN ID'S ALIAS IS `undefined` UNTIL SOMETHING HAS SAID IT — `null` is "it has none", which is
 * something said. Only what was said moves a URL: a page opened by its id stays on its id until its
 * row is read.
 *
 * ONE ALIAS, ONE ID. Remembering an alias for one id forgets it for whichever other id held it —
 * renamed elsewhere, and what it is called now is not known — so a stale entry never sends a link
 * to the wrong place. An alias the server says names nothing is forgotten too.
 */
export const useOperatorAliasStore = defineStore("operatorAliases", () => {
	/** By dataspace id. */
	const dataspaces = ref<Record<string, string | null>>({});
	/** By dataspace id, then process id. */
	const processes = ref<Record<string, Record<string, string | null>>>({});

	function rememberDataspace(id: string, alias: string | null) {
		if (alias) {
			for (const [other, held] of Object.entries(dataspaces.value)) {
				if (other !== id && held === alias) delete dataspaces.value[other];
			}
		}
		dataspaces.value[id] = alias;
	}

	function rememberDataspaces(rows: ReadonlyArray<{ id: string; alias: string | null }>) {
		for (const row of rows) rememberDataspace(row.id, row.alias);
	}

	function rememberProcess(dataspaceId: string, processId: string, alias: string | null) {
		const known = (processes.value[dataspaceId] ??= {});
		if (alias) {
			for (const [other, held] of Object.entries(known)) {
				if (other !== processId && held === alias) delete known[other];
			}
		}
		known[processId] = alias;
	}

	function rememberProcesses(
		dataspaceId: string,
		rows: ReadonlyArray<{ id: string; alias?: string | null }>,
	) {
		for (const row of rows) {
			if (row.alias !== undefined) rememberProcess(dataspaceId, row.id, row.alias);
		}
	}

	/** The dataspace's alias: `null` for none, `undefined` while nothing has said. */
	function dataspaceAlias(id: string): string | null | undefined {
		return dataspaces.value[id];
	}

	function processAlias(dataspaceId: string, processId: string): string | null | undefined {
		return processes.value[dataspaceId]?.[processId];
	}

	/** What a URL names the dataspace by: its alias, else its id. */
	function dataspaceSegment(id: string): string {
		return dataspaceAlias(id) ?? id;
	}

	function processSegment(dataspaceId: string, processId: string): string {
		return processAlias(dataspaceId, processId) ?? processId;
	}

	function dataspaceIdOf(alias: string): string | undefined {
		return Object.entries(dataspaces.value).find(([, held]) => held === alias)?.[0];
	}

	function processIdOf(dataspaceId: string, alias: string): string | undefined {
		return Object.entries(processes.value[dataspaceId] ?? {}).find(
			([, held]) => held === alias,
		)?.[0];
	}

	/**
	 * The id of the dataspace `segment` names as an alias, from what is known or else from the
	 * server; `null` when it names none the caller reaches. A failure that is not that answer is
	 * `null` too, and not remembered: the page then asks for the segment as an id, and is refused as
	 * for an unknown one.
	 */
	async function resolveDataspace(segment: string): Promise<string | null> {
		const alias = normalizeAlias(segment);
		const known = dataspaceIdOf(alias);
		if (known) return known;
		try {
			const { id } = await resolveDataspaceAlias(segment);
			rememberDataspace(id, alias);
			return id;
		} catch (e) {
			if (e instanceof ApiCallError && e.status === 404) forgetDataspaceAlias(alias);
			return null;
		}
	}

	/** As `resolveDataspace`, for a process of `dataspaceId`. */
	async function resolveProcess(dataspaceId: string, segment: string): Promise<string | null> {
		const alias = normalizeAlias(segment);
		const known = processIdOf(dataspaceId, alias);
		if (known) return known;
		try {
			const { id } = await resolveProcessAlias(dataspaceId, segment);
			rememberProcess(dataspaceId, id, alias);
			return id;
		} catch (e) {
			if (e instanceof ApiCallError && e.status === 404) {
				const held = processes.value[dataspaceId];
				for (const [other, value] of Object.entries(held ?? {})) {
					if (value === alias) delete held![other];
				}
			}
			return null;
		}
	}

	function forgetDataspaceAlias(alias: string) {
		for (const [other, held] of Object.entries(dataspaces.value)) {
			if (held === alias) delete dataspaces.value[other];
		}
	}

	/** A dataspace deleted: neither it nor its processes are named by anything any more. */
	function forgetDataspace(id: string) {
		delete dataspaces.value[id];
		delete processes.value[id];
	}

	return {
		dataspaces,
		processes,
		rememberDataspace,
		rememberDataspaces,
		rememberProcess,
		rememberProcesses,
		dataspaceAlias,
		processAlias,
		dataspaceSegment,
		processSegment,
		resolveDataspace,
		resolveProcess,
		forgetDataspace,
	};
});
